// FILE: /src/Components/Apps/CaptionOptionsPanel.js

import React from "react";
import { Checkbox, Input, Select, Switch } from "antd";

const { Option } = Select;
const { TextArea } = Input;

const normalizeBrandValue = (value, fallback = "Not set") => {
  if (!value) return fallback;
  return String(value);
};

const CaptionOptionsPanel = ({
  prompt,
  setPrompt,
  caption,
  setCaption,
  options,
  setOptions,
  brandKit,
  appCost,
}) => {
  const useBrandKit = Boolean(options.useBrandKit);

  const updateOption = (key, value) => {
    setOptions((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

    const totalCost = Number(
    appCost?.totalCredits !== undefined
      ? appCost.totalCredits
      : Number(appCost?.credits || 0) * Number(options.n || 1)
  );

  return (
    <div className="h-full min-h-0 flex flex-col gap-3">
      <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-extrabold text-gray-900 mb-0">
              Caption Brief
            </h2>
            <p className="text-xs text-gray-500 mb-0">
              Describe what you want the caption to say.
            </p>
          </div>

          <div className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100 px-2.5 py-1 text-xs font-semibold">
            <i className="fa-solid fa-bolt text-yellow-500"></i>
                        {totalCost} credit{totalCost === 1 ? "" : "s"}
            {appCost?.hasVideoInput ? " · video pricing" : ""}
          </div>
        </div>

        <div className="mt-3">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Prompt
          </label>
          <TextArea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            rows={4}
            placeholder="Example: Create a premium Instagram caption for our new summer collection."
            className="rounded-xl"
          />
        </div>

        <div className="mt-3">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Optional starting caption
          </label>
          <Input
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            placeholder="Example: New summer collection is live"
            className="rounded-xl"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex-1 min-h-0 overflow-y-auto">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-sm font-extrabold text-gray-900 mb-0">
              Style Options
            </h2>
            <p className="text-xs text-gray-500 mb-0">
              Use Brand Kit values or manually define style.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">
              Use Brand Kit
            </span>
            <Switch
              checked={useBrandKit}
              onChange={(checked) => updateOption("useBrandKit", checked)}
            />
          </div>
        </div>

        {useBrandKit && (
          <div className="mb-4 bg-purple-50 border border-purple-100 rounded-2xl p-3">
            <p className="text-xs font-bold text-purple-700 uppercase tracking-wide mb-2">
              Brand Kit Applied
            </p>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white border border-purple-100 rounded-xl p-2">
                <span className="block text-gray-400">Tone</span>
                <b className="text-gray-800">
                  {normalizeBrandValue(brandKit?.tone)}
                </b>
              </div>

              <div className="bg-white border border-purple-100 rounded-xl p-2">
                <span className="block text-gray-400">Language</span>
                <b className="text-gray-800">
                  {normalizeBrandValue(brandKit?.language)}
                </b>
              </div>

              <div className="bg-white border border-purple-100 rounded-xl p-2">
                <span className="block text-gray-400">Emoji</span>
                <b className="text-gray-800">
                  {normalizeBrandValue(brandKit?.emoji_level)}
                </b>
              </div>

              <div className="bg-white border border-purple-100 rounded-xl p-2">
                <span className="block text-gray-400">Length</span>
                <b className="text-gray-800">
                  {normalizeBrandValue(brandKit?.caption_sentence_length)}
                </b>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
              Platform
            </label>
            <Select
              value={options.platform}
              onChange={(value) => updateOption("platform", value)}
              className="w-full"
            >
              <Option value="Instagram">Instagram</Option>
              <Option value="Facebook">Facebook</Option>
              <Option value="LinkedIn">LinkedIn</Option>
              <Option value="Pinterest">Pinterest</Option>
              <Option value="YouTube">YouTube</Option>
              <Option value="TikTok">TikTok</Option>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
              Captions
            </label>
            <Select
              value={options.n}
              onChange={(value) => updateOption("n", value)}
              className="w-full"
            >
              <Option value={1}>1</Option>
              <Option value={2}>2</Option>
              <Option value={3}>3</Option>
              <Option value={5}>5</Option>
            </Select>
          </div>

          {!useBrandKit && (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                  Tone
                </label>
                <Select
                  value={options.tone}
                  onChange={(value) => updateOption("tone", value)}
                  className="w-full"
                >
                  <Option value="premium">Premium</Option>
                  <Option value="luxury">Luxury</Option>
                  <Option value="friendly">Friendly</Option>
                  <Option value="professional">Professional</Option>
                  <Option value="witty">Witty</Option>
                  <Option value="inspirational">Inspirational</Option>
                  <Option value="casual">Casual</Option>
                </Select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                  Emoji Level
                </label>
                <Select
                  value={options.emoji_density}
                  onChange={(value) => updateOption("emoji_density", value)}
                  className="w-full"
                >
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                </Select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                  Caption Length
                </label>
                <Select
                  value={options.caption_length}
                  onChange={(value) => updateOption("caption_length", value)}
                  className="w-full"
                >
                  <Option value="short">Short</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="long">Long</Option>
                </Select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                  Language
                </label>
                <Select
                  value={options.language}
                  onChange={(value) => updateOption("language", value)}
                  className="w-full"
                >
                  <Option value="English">English</Option>
                  <Option value="Hindi">Hindi</Option>
                  <Option value="Spanish">Spanish</Option>
                  <Option value="French">French</Option>
                  <Option value="German">German</Option>
                  <Option value="Arabic">Arabic</Option>
                </Select>
              </div>
            </>
          )}
        </div>

        <div className="mt-4">
          <Checkbox
            checked={options.generate_hashtags}
            onChange={(event) =>
              updateOption("generate_hashtags", event.target.checked)
            }
          >
            Generate hashtags
          </Checkbox>
        </div>
      </div>
    </div>
  );
};

export default CaptionOptionsPanel;